/**
 * Utility functions for downloading files (PDF and HTML).
 */

/**
 * Extract the URL string from a polymorphic file prop.
 */
export const getFileUrl = (file: string | File | { url: string }): string => {
  if (typeof file === 'string') return file;
  if (file instanceof File) return URL.createObjectURL(file);
  return file.url;
};

/**
 * Remove characters that are invalid in filenames.
 */
export const sanitizeFilename = (name: string): string => {
  return name
    .replace(/[/\\:*?"<>|]/g, '')
    .replace(/\s+/g, '_')
    .trim();
};

/**
 * Trigger a browser download from a URL.
 * - Blob URLs (same-origin): anchor + download attribute preserves the filename.
 * - External signed URLs (e.g. CloudFront/S3): window.location.href keeps the current page
 *   open when the server responds with Content-Disposition: attachment, avoiding the new-tab
 *   flash that target="_blank" causes.
 */
export const triggerDownload = (url: string, filename?: string): void => {
  if (url.startsWith('blob:')) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename ?? '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    window.location.href = url;
  }
};

/**
 * Download a file from a URL by fetching it as a blob. Used for PDF files on dislaying.
 */
export const downloadFromUrl = async (
  url: string,
  filename: string,
): Promise<void> => {
  const response = await fetch(url);
  const blob = await response.blob();
  downloadBlob(blob, filename);
};

/**
 * Trigger a browser download from a Blob. Used for HTML documents.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  URL.revokeObjectURL(url);
};
